import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { StreamDiagnostics } from '../types/diagnostics';

interface Props {
  diagnostics: StreamDiagnostics;
}

export const FlowTimeline: React.FC<Props> = ({ diagnostics }) => {
  const data = diagnostics.flowPoints.map((fp, i) => ({
    index: i,
    throughput: Math.round(fp.throughputMbps * 100) / 100,
    rtt: Math.round(fp.rttMs * 100) / 100,
    time: Math.round((fp.timestamp - diagnostics.flowPoints[0].timestamp) / 1000 * 10) / 10,
  }));

  return (
    <div className="bg-dark-900 rounded-xl p-6 border border-dark-700">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
          Flow Timeline
        </h3>
        <div className="flex gap-3 text-xs">
          <StatusBadge
            label="Congestion"
            active={diagnostics.congestionDetected}
            probability={diagnostics.congestionProbability}
          />
          <StatusBadge
            label="HoL Block"
            active={diagnostics.holBlockingDetected}
          />
          <StatusBadge
            label="ISP Shaping"
            active={diagnostics.ispShapingDetected}
          />
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis
            dataKey="time"
            stroke="#475569"
            fontSize={11}
            tickLine={false}
            label={{ value: 'Time (s)', position: 'insideBottom', offset: -2, fill: '#64748b', fontSize: 10 }}
          />
          <YAxis
            yAxisId="throughput"
            stroke="#22c55e"
            fontSize={11}
            tickLine={false}
            label={{ value: 'Mbps', angle: -90, position: 'insideLeft', fill: '#22c55e', fontSize: 10 }}
          />
          <YAxis
            yAxisId="rtt"
            orientation="right"
            stroke="#ef4444"
            fontSize={11}
            tickLine={false}
            label={{ value: 'ms', angle: 90, position: 'insideRight', fill: '#ef4444', fontSize: 10 }}
          />
          <Tooltip
            contentStyle={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            formatter={(v: number, name: string) => [
              name === 'throughput' ? `${v.toFixed(1)} Mbps` : `${v.toFixed(1)} ms`,
              name === 'throughput' ? 'Throughput' : 'Inter-chunk delay',
            ]}
          />
          <Legend />
          <Line
            yAxisId="throughput"
            type="monotone"
            dataKey="throughput"
            stroke="#22c55e"
            strokeWidth={2}
            dot={false}
            name="Throughput"
          />
          <Line
            yAxisId="rtt"
            type="monotone"
            dataKey="rtt"
            stroke="#ef4444"
            strokeWidth={1}
            dot={false}
            strokeOpacity={0.6}
            name="Delay"
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Stream metrics */}
      <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
        <div className="bg-dark-850 rounded-lg py-2">
          <div className="text-slate-500">Ramp-up</div>
          <div className="text-white font-semibold">
            {diagnostics.rampUpTimeMs > 0 ? `${(diagnostics.rampUpTimeMs / 1000).toFixed(1)}s` : '—'}
          </div>
        </div>
        <div className="bg-dark-850 rounded-lg py-2">
          <div className="text-slate-500">Slow Start</div>
          <div className="text-white font-semibold">
            {diagnostics.slowStartPhaseMs > 0 ? `${(diagnostics.slowStartPhaseMs / 1000).toFixed(1)}s` : '—'}
          </div>
        </div>
        <div className="bg-dark-850 rounded-lg py-2">
          <div className="text-slate-500">Stability</div>
          <div className={`font-semibold ${diagnostics.flowStability > 0.8 ? 'text-emerald-400' : diagnostics.flowStability > 0.5 ? 'text-yellow-400' : 'text-red-400'}`}>
            {Math.round(diagnostics.flowStability * 100)}%
          </div>
        </div>
        <div className="bg-dark-850 rounded-lg py-2">
          <div className="text-slate-500">Retransmission</div>
          <div className={`font-semibold ${diagnostics.retransmissionEstimate > 0.05 ? 'text-red-400' : 'text-emerald-400'}`}>
            {(diagnostics.retransmissionEstimate * 100).toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ label: string; active: boolean; probability?: number }> = ({
  label,
  active,
  probability,
}) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
      active
        ? 'bg-red-500/15 text-red-400 border border-red-500/30'
        : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-red-400' : 'bg-emerald-400'}`} />
    {label}
    {probability !== undefined && active && (
      <span className="text-[10px] opacity-70">{Math.round(probability * 100)}%</span>
    )}
  </span>
);
